import type { ConsentCategory, ConsentPreferences } from '@salesforce/storefront-next-runtime/events';
import { TrackingConsent } from '@/types/tracking-consent';
import { createLogger } from '@/lib/logger';

const logger = createLogger();

/** Default consent categories until a CMP (e.g. Cookiebot) is integrated. */
export const DEFAULT_CONSENT_CATEGORIES: ConsentCategory[] = ['necessary', 'analytics', 'marketing', 'personalization'];

/**
 * Return all configured consent categories as granted.
 * Used while no CMP is active; replace with CMP-driven preferences when Cookiebot is integrated.
 */
export function getGrantedConsentPreferences(
    consentCategories: ConsentCategory[] = DEFAULT_CONSENT_CATEGORIES
): ConsentPreferences {
    return consentCategories.length > 0 ? [...consentCategories] : ['necessary'];
}

type CookiebotConsent = { preferences?: boolean; statistics?: boolean; marketing?: boolean };

type CookiebotConsentWindow = Window & {
    Cookiebot?: { hasResponse?: boolean; consent?: CookiebotConsent };
};

/** The shopper's Cookiebot answer, or undefined until Cookiebot has loaded and the shopper has answered. */
export function getCookiebotConsent(): CookiebotConsent | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }
    const cookiebot = (window as CookiebotConsentWindow).Cookiebot;
    return cookiebot?.hasResponse ? (cookiebot.consent ?? {}) : undefined;
}

/** GTM loads Cookiebot asynchronously; don't hold events longer than this waiting for it. */
const COOKIEBOT_CONSENT_TIMEOUT_MS = 4000;

function waitForCookiebotResponse(): Promise<void> {
    if (typeof window === 'undefined' || (window as CookiebotConsentWindow).Cookiebot?.hasResponse) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        const done = () => {
            window.clearTimeout(timer);
            window.removeEventListener('CookiebotOnConsentReady', done);
            resolve();
        };
        const timer = window.setTimeout(done, COOKIEBOT_CONSENT_TIMEOUT_MS);
        window.addEventListener('CookiebotOnConsentReady', done);
    });
}

/**
 * Engagement adapter consent from Cookiebot (Cookiebot categories mapped to adapter categories).
 * Waits for Cookiebot to answer so landing-page events of returning shoppers aren't lost; only
 * `necessary` is granted when there is no answer, so category-gated adapters (e.g. Einstein on
 * `marketing`) never fire without consent.
 */
export async function getCookiebotConsentPreferences(): Promise<ConsentPreferences> {
    await waitForCookiebotResponse();
    const consent = getCookiebotConsent();
    return [
        'necessary',
        ...(consent?.statistics ? ['analytics'] : []),
        ...(consent?.marketing ? ['marketing'] : []),
        ...(consent?.preferences ? ['personalization'] : []),
    ];
}

/**
 * Check whether there is sufficient consent to send events.
 *
 * Returns true (allow) when:
 * - The user granted at least one category and the adapter has no consentCategory
 *   configured (adapter doesn't require specific consent)
 * - The adapter's consentCategory is included in the consent preferences
 *
 * Returns false when:
 * - No consentPreferences are provided (undefined means consent state is unknown or
 *   not yet determined — the safe default is to block)
 * - consentPreferences is an empty array (user explicitly declined all tracking)
 * - The adapter's consentCategory is not in the granted preferences
 *
 * @param adapterConsentCategory - The consent category configured on the adapter
 * @param consentPreferences - The consent preferences from the consent system
 * @returns true if there is sufficient consent, false otherwise
 */
export function hasConsent(adapterConsentCategory?: ConsentCategory, consentPreferences?: ConsentPreferences): boolean {
    // Consent state unknown or not yet determined — block
    if (!consentPreferences) {
        return false;
    }
    // User explicitly declined all tracking
    if (consentPreferences.length === 0) {
        return false;
    }
    // Adapter doesn't require a specific category — allow
    if (!adapterConsentCategory) {
        return true;
    }
    return consentPreferences.includes(adapterConsentCategory);
}

/**
 * Build consent preferences from the binary TrackingConsent (DNT) and configured consent categories.
 *
 * This bridges the existing binary consent system with the per-adapter granular
 * consent model. When the consent UI is extended to support category-level choices,
 * this function can be updated to return a subset of categories.
 *
 * @param trackingConsent - The visitor's binary consent status
 * @param consentCategories - The configured consent categories (from config.engagement.analytics.trackingConsent.consentCategories)
 * @param isTrackingConsentEnabled - Whether the tracking consent system is active
 * @returns ConsentPreferences with granted categories, empty array if declined, or undefined if not yet determined.
 *          When undefined is returned, the hook layer (useAnalytics / PageViewTracker) blocks all tracking.
 */
export function buildConsentPreferences(
    trackingConsent: TrackingConsent | undefined,
    consentCategories: ConsentCategory[],
    isTrackingConsentEnabled: boolean
): ConsentPreferences | undefined {
    // Consent system not active — allow all tracking unrestricted
    if (!isTrackingConsentEnabled) {
        return consentCategories.length > 0 ? [...consentCategories] : ['necessary'];
    }
    if (trackingConsent === TrackingConsent.Accepted) {
        if (consentCategories.length === 0) {
            logger.warn(
                'Tracking consent is enabled but consentCategories is empty. ' +
                    'Analytics events will be blocked. Add categories to ' +
                    'config.engagement.analytics.trackingConsent.consentCategories.'
            );
        }
        return [...consentCategories];
    }
    if (trackingConsent === TrackingConsent.Declined) {
        return [];
    }
    // Consent enabled but not yet determined — block tracking
    return undefined;
}
